import type { IncomingHttpHeaders } from 'http';
import type { ThemeTypes } from '../../types/Theme.namespace';
import type {
  PhotoView,
  DealerSummary,
  CURRENCIES,
  ISearchPageFilterValue,
} from '../../types';
import type { FeaturedDealer } from '../../api/types/adviewApiTypes';
import { IFinancePricePerMonthSearchModel } from '../../api/types/accountsApiTypes';
import { IMerits } from '../../types';
import { DisplayAttribute } from '../../api/types/dealerApiTypes';
import { GTMEvent } from '../../services/gtmService';
import type { PricingIndicatorStatus } from '../../components/PricingIndicator/PricingIndicatorIconText/PricingIndicatorIconText';

export type TypePrice = 'price' | 'pricePerMonth';

export type GeoFilter = {
  lat: number;
  lon: number;
  rad: number;
  countyTown: string | null;
  county: string | null;
};

export interface IGetAdsRequestFilter {
  name: string;
  values: string[];
}
export interface IGetAdsRequestRange {
  name: string;
  to?: string;
  from?: string;
  currency?: CURRENCIES;
  typePrice?: TypePrice;
}
export interface IMakeModelFilters {
  make: string;
  model: string;
  trim?: string;
}

export interface IGetAdsRequest {
  geoFilter?: GeoFilter;
  filters: IGetAdsRequestFilter[];
  andFilters?: IGetAdsRequestFilter[];
  paging: {
    from: number;
    pageSize: number;
  };
  makeModelFilters?: IMakeModelFilters[];
  ranges?: IGetAdsRequestRange[];
  sections: string[];
  sort?: string;
  terms?: string | null;
  saved?: string;
  alertId?: string;
}

export interface ICardPayment {
  stripeAccountId?: string;
  quantity?: string;
  shippingFee?: string;
  shippingAvailable?: boolean;
  cardPaymentType?: string;
  acceptPayment?: boolean;
}

export interface ISearchPageAds {
  currency: CURRENCIES;
  county: string;
  countyTown?: string;
  id: number;
  header: string;
  userSaved: boolean;
  photos?: PhotoView[];
  price: string;
  oldPriceView: {
    showOldPrice: boolean;
    oldPrice?: string | null;
  };
  section: {
    displayName: string;
    name: string;
  };
  wanted: boolean;
  age: string;
  friendlyUrl: string;
  deliveryAvailable: boolean;
  mediaCount: number;
  spotlight: boolean;
  spotlightable: boolean;
  herdwatchVerified: boolean;
  dealer?: DealerSummary;
  highlight?: ThemeTypes.Colors;
  highlightText?: string;
  infoLabel?: string;
  priceOnRequest: boolean;
  imageAlt?: string;
  keyInfo?: string[];
  manufacturerApprovedBadge?: { badgeUrl?: string; manufacturer?: string };
  meritsV2?: IMerits[];
  specialOffer?: string;
  greenlightVerified?: boolean;
  cardPayments?: ICardPayment[];
  youTubeVideoId?: string;
  link360?: string;
  link360Interior?: string;
  userId?: number;
  dealerId?: number;
  state?: string;
  publishDate?: number;
  emailResponse?: boolean;
  phoneResponse?: boolean;
  forSale?: boolean;
  c2b?: boolean;
  displayAttributes?: DisplayAttribute[];
  financeSummary?: IFinancePricePerMonthSearchModel;
  bumpable?: boolean;
  adAnalytics?: boolean;
  relativeUrl?: string;
}

export type SearchPageBreadcrumbs = {
  name: string;
  displayName: string;
  id: number;
  title: string;
  subdomain: string;
  containsSubsections: boolean;
  xtn2: number;
};

export interface ISearchPagePaging {
  totalPages: number;
  currentPage: number;
  nextFrom: number;
  previousFrom: number;
  displayingFrom: number;
  displayingTo: number;
  totalResults: number;
  pageSize: number;
}

export interface ISearchPageSeoData {
  internalLinks?: {
    text: string;
    url: string;
  }[];
  type?: string;
}

export interface IDFP {
  area: string[];
  section: string[];
  user_id: string[];
  vertical: string[];
  words: string[];
}
export interface IAdSense {
  pubId: string;
  styleId: string;
  query: string;
  location?: boolean;
  siteLinks?: boolean;
}

export type SeoFaqSchema = {
  avgPrice: string;
  avgMileage: string;
  topYear: string;
  topFuelType: string;
  topColor: string;
  newCar: boolean;
};

export interface IGetAdsResponse {
  paging: ISearchPagePaging;
  breadcrumbs: SearchPageBreadcrumbs[];
  dataTargetingProps: {
    dfp: IDFP;
    gtm: GTMEvent;
  };
  ads?: ISearchPageAds[];
  spotlights?: ISearchPageAds[];
  seoData?: ISearchPageSeoData;
  promoSlot?: FeaturedDealer;
  seoFaqSchema?: {
    faqSchema: SeoFaqSchema;
  };
  recommendations?: ISearchPageAds[];
}

// Temporarily support dedicated v2 response. Once the migration is done and rolled out, this will become default.
// TODO: Treat this as default response and remove IGetAdsResponseV2 bane once the migration is done.
export interface IGetAdsResponseV2 {
  paging: ISearchPagePaging;
  breadcrumbs: SearchPageBreadcrumbs[];
  dataTargetingProps: {
    dfp: IDFP;
    gtm: GTMEvent;
  };
  ads?: Ad[];
  spotlights?: Ad[];
  seoData?: ISearchPageSeoData;
  promoSlot?: FeaturedDealer;
  seoFaqSchema?: {
    faqSchema: SeoFaqSchema;
  };
  recommendations?: ISearchPageAds[];
}

// Ranges UI
export interface ISearchPageFilterRangesValue {
  from: ISearchPageFilterValue[];
  to: ISearchPageFilterValue[];
  valueType: string;
}
// MakeModel UI
export interface ISearchPageFilterMakeModelValue {
  all: ISearchPageFilterValue[];
  popular: ISearchPageFilterValue[];
  valueType: string;
}

// API structure not UI
export interface ISearchPageFilter {
  displayHint?: string | null;
  displayName: string;
  filterType: {
    id: number | null;
    name: string;
  };
  type?: 'filter' | 'group';
  leadingIcon?: {
    name: string;
    url: string;
  };
  trailingIcon?: {
    name: string;
    url: string;
    text?: string;
  };
  hideDisplayName?: boolean;
  id: number | null;
  maxLength?: number | null;
  name: string;
  searchQueryGroup?:
    | 'filters'
    | 'ranges'
    | 'makeModel'
    | 'terms'
    | 'andFilters';
  values: ISearchPageFilterValue[] | null;
  variant:
    | 'DONEDEAL_DEFAULT'
    | 'DONEDEAL_TAG'
    | 'DONEDEAL_RADIO'
    | 'DONEDEAL_IMAGE'
    | 'DONEDEAL_MAKE_MODEL'
    | 'DONEDEAL_PRICE_DROPDOWN'
    | 'DONEDEAL_PRICE_DROPDOWN_PPM'
    | 'DONEDEAL_PRICE_CHECKBOX'
    | 'HIDDEN'
    | 'DONEDEAL_CHECKBOX';
  subFilters?: ISearchPageFilter[] | null;
  openByDefault?: boolean;
  showCount?: boolean;
}

export interface ISortField {
  displayName: string;
  name: string;
}

export interface IGetFiltersResponse {
  name: string;
  showByDefault: ISearchPageFilter[];
  sortFields: ISortField[];
}

export type TSaveableSearch = boolean;

export interface IGetSection {
  name: string;
  displayName: string;
  parentName?: string | null;
  parentDisplayName?: string | null;
  searchMatches: number;
  subSections?: IGetSection[];
}

type GetFiltersWithCountRequest = {
  section: string;
  headers?: IncomingHttpHeaders;
  payload?: IGetAdsRequest;
  timeout?: number;
};

type GetModelsWithCountsRequest = {
  section: string;
  make: string;
  payload: IGetAdsRequest;
  headers?: IncomingHttpHeaders;
  timeout?: string;
};

type GetTrimsWithCountsRequest = {
  section: string;
  make: string;
  model: string;
  payload: IGetAdsRequest;
  headers?: IncomingHttpHeaders;
  timeout?: string;
};

export const SELLER_TYPE = {
  PRIVATE: 'Private Seller',
  TRADER: 'Trader',
} as const;

export interface Seller {
  type: keyof typeof SELLER_TYPE;
  rating?: number;
}

/* Types related to updates to ads array in Search V2 */
export type Ad = {
  id: number;
  title: string;
  section: {
    displayName: string;
    name: string;
  };
  county?: string;
  countyTown?: string;
  age: string;
  friendlyUrl: string;
  relativeUrl: string;
  imageAlt?: string;
  metaInfo: string[];
  tags?: Tags[];
  tagsV2?: Tags[];
  priceInfo: PriceInfo;
  gallery?: {
    coverImage: ImageUrls;
    thumbnails?: Thumbnails[];
    totalMediaCount: number;
  };
  dealer?: Dealer;
  seller?: Seller;
  saved: boolean;
  spotlighted: boolean;
  isFreeHistoryCheckAd?: boolean;
  priceIndicator?: { state: PricingIndicatorStatus };
};

type PriceInfo = {
  previousPrice?: string;
  price: string;
  financePPM?: string;
  priceOnRequest: boolean;
};

type ImageUrls = {
  small: string;
  small2: string;
  medium: string;
  large: string;
  smallWebp: string;
  small2Webp: string;
  mediumWebp: string;
  largeWebp: string;
  xLarge: string;
  xLargeWebp: string;
};

type Tags = {
  displayName: string;
  type: TagType;
};

type Thumbnails = {
  type: ThumbnailType;
  photo?: ImageUrls;
  url?: string;
};

type Gallery = {
  coverImage: ImageUrls;
  thumbnails?: Thumbnails[];
  totalMediaCount: number;
};

type TagType = 'default' | 'greenlight' | 'freeHistoryReport';

type ThumbnailType = 'video' | '360' | 'photo';

export type Dealer = {
  logo?: {
    small: string;
    medium: string;
  };
  name: string;
  type: string;
  rating?: number;
  trusted?: boolean;
};

export type {
  GetFiltersWithCountRequest,
  GetModelsWithCountsRequest,
  GetTrimsWithCountsRequest,
  Thumbnails,
  Gallery,
};
